import { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Box, Typography, TableCell } from '@mui/material';

// comps
import GlobalInput from '../../components/global/input';
import GlobalButton from '../../components/global/button';
import DataGrid from '../../components/grid/dataGrid';
import { CustomTableInputCell } from '../../components/grid/CustomTableCell';

import { colors } from '../../theme';

// api
import Message from '../../models/special-message/message';
import MessagesApi from '../../api/messages.api';
import { useAppSelector } from '../../hooks/hooks';

// icons
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

// constants
import { MOMENT_DATE_FORMAT, MOMENT_DATE_DISPLAY_FORMAT } from '../../constants/date_format';
import { ACADEMIC_VIEW_TYPE, FACILITIES_VIEW_TYPE } from '../../constants';

const SpecialMessages = (props: any) => {
    const appState = useAppSelector((state) => state.appState);
    const _messagesApi = new MessagesApi();

    const [state, setState] = useState({
        messages: [] as Array<Message>,
        createItem: {
            date: null,
            message: null,
        },
        filterObject: {
            message: null,
            startDate: null,
            endDate: null,
        },
        isClearAll: false,
        isFilteredGrid: false,
    });

    const [resetPage, setResetPage] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);

    const loadMessages = async (campusId?: number, schoolId?: number) => {
        const messages = await _messagesApi.GetAll(appState.viewType === FACILITIES_VIEW_TYPE ? campusId : null, appState.viewType === ACADEMIC_VIEW_TYPE ? schoolId : null);

        const messageModels = messages.map((m) => {
            let message = m.message;
            if (m.message.includes('  ')) {
                message = message.replace(/ +(?= )/g, '');
            }
            return new Message(m.id, message, m.date, m.lastUpdatedBy, m.schoolId, m.campusId, appState.viewType);
        });

        setState({
            ...state,
            messages: messageModels.sort((a: any, b: any) => {
                return b.date - a.date;
            }),
            createItem: {
                ...state.createItem,
                date: moment().format(MOMENT_DATE_FORMAT),
            },
        });
    };

    useEffect(() => {
        const { campus, school } = appState;
        if (campus?.id || school?.id) {
            loadMessages(campus?.id, school?.id).catch((error) => toast.error('Error loading messages'));
        }
    }, [appState]);

    const handleOnChange = (name: any, value: any, changeObj: any) => {
        if (value === '') value = null;
        if (changeObj === 'filterObject') setButtonDisabled(false);
        setState({
            ...state,
            [changeObj]: {
                ...state[changeObj],
                [name]: value,
            },
        });
    };

    const handleClearAll = () => {
        let filterObject = { ...state.filterObject };
        Object.keys(filterObject).forEach((i) => (filterObject[i] = ''));

        setState({
            ...state,
            filterObject,
            isClearAll: true,
            isFilteredGrid: false,
        });
        setResetPage(true);
        setButtonDisabled(true);
    };

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '' || !o)) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        setState({ ...state, isClearAll: false });
    }, [state.isClearAll]);

    const handleCreateMessage = async () => {
        const today = moment().format(MOMENT_DATE_FORMAT);

        // Guard against submitting an empty message. Without this the API is called with
        // message: null and answers 400 "Server Error", which tells the user nothing.
        if (!state.createItem.message?.trim()) {
            toast.error('Please enter a message before creating.');
            return;
        }

        if (!state.createItem.date) {
            toast.error('Please select a date before creating.');
            return;
        }

        if (moment(state.createItem.date).isBefore(today)) {
            toast.error('Unable to create a special message for past date(s).');
            return;
        } else {
            const message = new Message(null, state.createItem.message, state.createItem.date, null, appState.school?.id, appState.campus?.id, appState.viewType);

            try {
                const savedMessageDto = await _messagesApi.SaveMessage(message);
                const savedMessage = Message.fromDto(savedMessageDto, appState.viewType);

                setState({
                    ...state,
                    messages: [savedMessage, ...state.messages],

                    createItem: {
                        // '' not null: a null value makes the MUI input uncontrolled, so the
                        // box keeps displaying the old text while state says there is none.
                        message: '',
                        date: today,
                    },
                });
                toast.success('Message successfully created.');
            } catch (error) {
                toast.error('Error creating message');
            }
        }
    };

    const handleMessageSave = async (message: Message): Promise<boolean> => {
        try {
            await _messagesApi.SaveMessage(message);
            return true;
        } catch (error) {
            toast.error('Error saving message');
            return false;
        }
    };

    const handleMessageDelete = async (message: Message): Promise<boolean> => {
        try {
            await _messagesApi.DeleteMessage(message.id);
            const messages = state.messages.filter((m) => m.id !== message.id);

            setState({
                ...state,
                messages,
            });
            toast.success('Message successfully deleted.');
            return true;
        } catch (error) {
            toast.error('Error deleting message');
            return false;
        }
    };

    const getGridData = () => {
        let data = [...state.messages];
        const { message, startDate, endDate } = state.filterObject;

        if (state.isFilteredGrid) {
            if (message) data = data.filter((item) => item?.message.toLowerCase().includes(message.toLowerCase()));
            if (startDate) {
                if (moment(endDate).isBefore(startDate)) {
                    toast.error('Selected date is past Start Date.');
                    return;
                }
                // TODO: remove moment if returned data is in moment format
                data = data.filter((d) => moment(d.date).isSameOrAfter(moment(startDate)));

                if (endDate) data = data.filter((d) => moment(d.date).isSameOrAfter(moment(startDate)) && moment(d.date).isSameOrBefore(moment(endDate)));
            }
        }
        return data;
    };

    const renderSpecialMsgGrid = (data: any, onChange: any) => {
        const { date, lastUpdatedBy } = data;

        return (
            <>
                <TableCell sx={{ width: '14%' }}>{moment(date).format(MOMENT_DATE_DISPLAY_FORMAT)}</TableCell>
                <CustomTableInputCell data={data} name={'message'} onChange={onChange} />
                <TableCell sx={{ width: '20%' }}>{lastUpdatedBy}</TableCell>
            </>
        );
    };

    return (
        <PageContainer>
            <Actions sx={{ flex: 1 }}>
                <ActionContainer>
                    <Typography variant="h4">Add a new special message</Typography>
                    <Label>Date</Label>
                    {/* `?? ''` keeps these inputs controlled. handleOnChange stores '' as null, and a
                        null value hands the field back to the DOM, so state and UI silently diverge. */}
                    <GlobalInput type="date" name="date" value={state.createItem.date ?? ''} onChange={(name, value) => handleOnChange(name, value, 'createItem')} />
                    <Label>Message</Label>
                    <GlobalInput
                        multiline
                        minRows={3}
                        name="message"
                        placeholder="Special Message (Max 100 characters)"
                        inputProps={{ maxlength: 100 }}
                        value={state.createItem?.message ?? ''}
                        onChange={(name, value) => handleOnChange(name, value, 'createItem')}
                    />
                    <div>
                        <GlobalButton text="create" variant="contained" onClick={handleCreateMessage} />
                    </div>
                </ActionContainer>
                <ActionContainer>
                    <Typography variant="h4">Filter messages</Typography>
                    <Label>Message</Label>
                    <GlobalInput
                        name="message"
                        icon={<FilterIcon />}
                        placeholder="Special message to filter by"
                        value={state.filterObject?.message ?? ''}
                        onChange={(name, value) => handleOnChange(name, value, 'filterObject')}
                    />
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                            <Label>Start Date</Label>
                            <GlobalInput
                                type="date"
                                name="startDate"
                                sx={{ marginRight: '5px', width: '100%' }}
                                value={state.filterObject?.startDate ?? ''}
                                onChange={(name, value) => handleOnChange(name, value, 'filterObject')}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Label>End Date</Label>
                            <GlobalInput
                                type="date"
                                name="endDate"
                                value={state.filterObject?.endDate ?? ''}
                                onChange={(name, value) => handleOnChange(name, value, 'filterObject')}
                                sx={{ width: '100%' }}
                            />
                        </div>
                    </div>
                    <FlexBox>
                        <GlobalButton text="clear all" variant="round" secondary={'true'} endIcon={<RefreshIcon />} style={{ width: '30%' }} onClick={handleClearAll} disabled={buttonDisabled} />
                        <GlobalButton variant="round" text="search" disabled={buttonDisabled} fullWidth style={{ width: '30%' }} onClick={() => setState({ ...state, isFilteredGrid: true })} />
                    </FlexBox>
                </ActionContainer>
            </Actions>
            <Box sx={{ flex: 2.5 }}>
                <DataGrid
                    isEdit
                    isDelete
                    topHeight={10}
                    columns={specialMsgGridColumns}
                    data={getGridData()}
                    renderRows={renderSpecialMsgGrid}
                    dataItemKey="id"
                    isClearSorting={state.isClearAll}
                    resetPage={resetPage}
                    onHeaderClick={() => {
                        setButtonDisabled(false);
                    }}
                    // deleteDialog props
                    onSaveAsync={handleMessageSave}
                    deleteDialogProps={{
                        isWarning: true,
                        buttonText: 'delete',
                        deleteItem: 'the selected message',
                    }}
                    onDelete={handleMessageDelete}
                />
            </Box>
        </PageContainer>
    );
};

export default SpecialMessages;

const Label = styled.div`
    color: #0085E6;
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 2px !important;

    & > span {
        color: #D73948;
    }
`

const PageContainer = styled.div`
    display: flex;
    align-items: flex-start;
`;

const Actions = styled(Box)`
    && {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-right: 15px;
        margin-top: 10px;

        & > div:first-of-type {
            margin-bottom: 15px;
        }
    }
`;

const ActionContainer = styled.div`
    background: ${colors.white};
    border-radius: 5px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
    padding: 12px;

    & > * {
        margin-bottom: 15px;
    }

    h4 {
        color: ${colors.blue.main};
        padding-bottom: 5px;
    }

    & > :last-child {
        display: flex;
        justify-content: flex-end;
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;

const specialMsgGridColumns = [
    {
        id: 'date',
        label: 'Date',
    },
    {
        id: 'message',
        label: 'Special Message',
    },
    {
        label: 'Last Updated By',
        disabled: true,
    },
];
